import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Icon } from "@iconify/react";
import style from "../../../scss/Conteo.module.scss";
import SelectComponent from "../../../components/SelectComponent";
import CampoVotosComponent from "./CampoVotosComponent";
import FotoE14Component from "./FotoE14Component";
import ModalCierreComponent from "./ModalCierreComponent";
import useCatalogos from "../../../hooks/useCatalogos";
import {
  cargarFotoEscrutinio,
  cerrarEscrutinio,
  getEscrutinioMesa,
  getFotoEscrutinio,
  guardarEscrutinio,
  reabrirEscrutinio,
} from "../../../Api/ApiEscrutinio";
import { TIPOS_CANDIDATO, etiquetaTipoCandidato } from "../../../constants/tiposCandidato";
import { calcularNivelacion } from "../../../utils/nivelacionEscrutinio";
import { comprimirImagen } from "../../../utils/imagen";
import { obtenerMensajeError } from "../../../utils/mensajeError";
import { esAdmin } from "../../../utils/sesion";
import { encolar, esErrorDeRed } from "../../../utils/colaSincronizacion";
import { EVENTO_PENDIENTES } from "../../../utils/almacenLocal";
import {
  buscarEscrutinioPendiente,
  guardarCopiaEscrutinio,
  guardarEscrutinioEnEquipo,
  leerCopiaEscrutinio,
  registroDesdePendiente,
} from "../../../utils/escrutinioSinConexion";

// los campos del acta se manejan como texto mientras se digitan
const TOTALES_VACIOS = {
  totalVotantesE11: "",
  totalVotosUrna: "",
  votosIncinerados: "0",
  votosBlanco: "",
  votosNulos: "",
  votosNoMarcados: "",
};

const CAMPOS_NIVELACION = [
  { campo: "totalVotantesE11", etiqueta: "Total votantes formulario E-11" },
  { campo: "totalVotosUrna", etiqueta: "Total votos en la urna" },
  { campo: "votosIncinerados", etiqueta: "Total votos incinerados" },
];

const CAMPOS_ESPECIALES = [
  { campo: "votosBlanco", etiqueta: "Votos en blanco" },
  { campo: "votosNulos", etiqueta: "Votos nulos" },
  { campo: "votosNoMarcados", etiqueta: "Votos no marcados" },
];

const alertaExito = (texto) => Swal.fire({ icon: "success", text: texto, timer: 1800, showConfirmButton: false });
const alertaError = (error) => Swal.fire({ icon: "error", text: obtenerMensajeError(error, error?.message) });
const alertaEnEquipo = (texto) =>
  Swal.fire({ icon: "info", title: "Guardado sin conexión", text: texto });

const EscrutinioComponent = () => {
  const admin = esAdmin();
  const { candidatos, opcionesLugar, opcionesMesaDe } = useCatalogos();
  const formRef = useRef(null);

  // la mesa puede llegar en la URL (?corporacion=&lugar=&mesa=) desde Resultados
  const [parametros, setParametros] = useSearchParams();
  const [corporacion, setCorporacion] = useState(() => {
    const valor = parametros.get("corporacion");
    return TIPOS_CANDIDATO.some((tipo) => tipo.value === valor) ? valor : "";
  });
  const [lugar, setLugar] = useState(() => parametros.get("lugar"));
  const [mesa, setMesa] = useState(() => parametros.get("mesa"));
  const [escrutinio, setEscrutinio] = useState(null);
  const [totales, setTotales] = useState(TOTALES_VACIOS);
  const [votos, setVotos] = useState({});
  // undefined: sin cambios; data URL: foto nueva aún sin guardar
  const [fotoNueva, setFotoNueva] = useState(undefined);
  const [fotoGuardada, setFotoGuardada] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [bloqueo, setBloqueo] = useState("");
  const [mostrarCierre, setMostrarCierre] = useState(false);
  // true: no hubo conexión al abrir la mesa y se muestran datos del equipo
  const [sinConexion, setSinConexion] = useState(false);
  // se incrementa para volver a cargar la mesa desde el servidor
  const [recarga, setRecarga] = useState(0);

  const mesaSeleccionada = Boolean(corporacion && lugar && mesa);
  const cerrado = escrutinio?.estado === "cerrado";
  const nombreCorporacion = corporacion ? etiquetaTipoCandidato(corporacion) : "";
  const fotoVista = fotoNueva ?? fotoGuardada;
  const escrutinioId = escrutinio?.id;
  const tieneFoto = escrutinio?.tieneFoto;
  const pendienteLocal = Boolean(escrutinio?.pendiente);

  const candidatosCorporacion = useMemo(
    () => candidatos.filter((c) => c.tipo === corporacion),
    [candidatos, corporacion]
  );
  const nivelacion = calcularNivelacion(
    totales,
    candidatosCorporacion.map((c) => votos[c.id])
  );

  // pasa el registro del backend al formulario (null = acta nueva)
  const cargarEnFormulario = useCallback((registro) => {
    setEscrutinio(registro);
    setFotoNueva(undefined);
    setTotales(
      registro
        ? Object.fromEntries(Object.keys(TOTALES_VACIOS).map((campo) => [campo, String(registro[campo])]))
        : TOTALES_VACIOS
    );
    setVotos(registro ? Object.fromEntries(registro.votos.map((v) => [v.candidatoId, String(v.votos)])) : {});
  }, []);

  // la URL refleja la mesa abierta: se puede recargar o compartir el enlace
  useEffect(() => {
    const nuevos = new URLSearchParams();
    if (corporacion) nuevos.set("corporacion", corporacion);
    if (lugar) nuevos.set("lugar", lugar);
    if (mesa) nuevos.set("mesa", mesa);
    // solo navega si cambió (evita un ciclo con setParametros)
    if (nuevos.toString() !== parametros.toString()) {
      setParametros(nuevos, { replace: true });
    }
  }, [corporacion, lugar, mesa, parametros, setParametros]);

  // carga un acta guardada en el equipo (aún sin enviar) sobre la copia del servidor
  const cargarPendiente = useCallback(
    (pendiente, base) => {
      cargarEnFormulario(registroDesdePendiente(pendiente, base));
      if (pendiente.datos.foto) setFotoGuardada(pendiente.datos.foto);
    },
    [cargarEnFormulario]
  );

  // al cambiar corporación, lugar o mesa se carga el escrutinio que ya exista.
  // Lo guardado en el equipo y aún no enviado tiene prioridad (es lo más nuevo);
  // sin conexión se usa la última copia del servidor guardada en el equipo.
  useEffect(() => {
    cargarEnFormulario(null);
    setFotoGuardada(null);
    setBloqueo("");
    setSinConexion(false);
    if (!mesaSeleccionada) return undefined;

    const datosMesa = { corporacion, lugar, mesa };
    let vigente = true;
    setCargando(true);
    (async () => {
      const pendiente = await buscarEscrutinioPendiente(datosMesa);
      try {
        const { escrutinio: registro } = await getEscrutinioMesa(corporacion, lugar, mesa);
        if (!vigente) return;
        if (registro) guardarCopiaEscrutinio(datosMesa, registro);
        if (pendiente) cargarPendiente(pendiente, registro);
        else cargarEnFormulario(registro);
      } catch (error) {
        if (!vigente) return;
        if (!esErrorDeRed(error)) {
          setBloqueo(obtenerMensajeError(error));
          return;
        }
        const copia = await leerCopiaEscrutinio(datosMesa);
        if (!vigente) return;
        setSinConexion(true);
        if (pendiente) cargarPendiente(pendiente, copia);
        else cargarEnFormulario(copia);
      }
    })().finally(() => vigente && setCargando(false));

    return () => {
      vigente = false;
    };
  }, [corporacion, lugar, mesa, mesaSeleccionada, cargarEnFormulario, cargarPendiente, recarga]);

  // cuando el acta de esta mesa que estaba en el equipo ya se envió, se
  // recarga desde el servidor (quita los avisos de "pendiente" y "sin conexión")
  useEffect(() => {
    if (!pendienteLocal) return undefined;
    const revisarEnvio = async () => {
      if (!(await buscarEscrutinioPendiente({ corporacion, lugar, mesa }))) setRecarga((n) => n + 1);
    };
    window.addEventListener(EVENTO_PENDIENTES, revisarEnvio);
    return () => window.removeEventListener(EVENTO_PENDIENTES, revisarEnvio);
  }, [pendienteLocal, corporacion, lugar, mesa]);

  useEffect(() => {
    const alConectar = () => setSinConexion(false);
    window.addEventListener("online", alConectar);
    return () => window.removeEventListener("online", alConectar);
  }, []);

  // la foto se pide aparte porque no viaja con el escrutinio
  useEffect(() => {
    // si hay un acta del equipo sin enviar, su foto es la vigente
    if (!escrutinioId || !tieneFoto || pendienteLocal) return undefined;
    let vigente = true;
    getFotoEscrutinio(escrutinioId)
      .then(({ foto }) => vigente && setFotoGuardada(foto))
      .catch((error) => console.error(error));
    return () => {
      vigente = false;
    };
  }, [escrutinioId, tieneFoto, pendienteLocal]);

  const cambiarTotal = (campo) => (valor) => setTotales((previos) => ({ ...previos, [campo]: valor }));
  const cambiarVotos = (candidatoId) => (valor) => setVotos((previos) => ({ ...previos, [candidatoId]: valor }));

  // abierta: la foto se envía al guardar. Cerrada sin foto: se carga de una vez
  const seleccionarFoto = async (archivo) => {
    try {
      const foto = await comprimirImagen(archivo);
      if (!cerrado) {
        setFotoNueva(foto);
        return;
      }
      setProcesando(true);
      // acta cerrada que aún está en el equipo: la foto se suma a lo que se enviará
      if (pendienteLocal) {
        cargarPendiente(await guardarEscrutinioEnEquipo({ corporacion, lugar, mesa, foto }), escrutinio);
        alertaEnEquipo("La foto quedó guardada en este equipo junto con el acta.");
        return;
      }
      try {
        const respuesta = await cargarFotoEscrutinio(escrutinio.id, foto);
        setFotoGuardada(foto);
        setEscrutinio(respuesta.escrutinio);
        alertaExito(respuesta.msg);
      } catch (error) {
        if (!esErrorDeRed(error)) throw error;
        await encolar("fotoEscrutinio", { id: escrutinio.id, foto }, `foto:${escrutinio.id}`);
        setFotoGuardada(foto);
        setEscrutinio({ ...escrutinio, tieneFoto: true });
        alertaEnEquipo("La foto quedó guardada en este equipo y se enviará cuando vuelva la señal.");
      }
    } catch (error) {
      alertaError(error);
    } finally {
      setProcesando(false);
    }
  };

  // Guarda el acta (y la cierra si llega "cierre"). Sin conexión queda en el
  // equipo y se envía sola al volver la señal: { msg, enEquipo }
  const guardar = async (cierre = null) => {
    const datos = {
      corporacion,
      lugar,
      mesa,
      ...Object.fromEntries(Object.entries(totales).map(([campo, valor]) => [campo, Number(valor)])),
      votos: candidatosCorporacion.map((c) => ({ candidatoId: c.id, votos: Number(votos[c.id]) })),
      ...(fotoNueva !== undefined && { foto: fotoNueva }),
    };

    try {
      let respuesta = await guardarEscrutinio(datos);
      if (cierre) respuesta = await cerrarEscrutinio(respuesta.escrutinio.id, cierre);
      if (fotoNueva !== undefined) setFotoGuardada(fotoNueva);
      cargarEnFormulario(respuesta.escrutinio);
      guardarCopiaEscrutinio(datos, respuesta.escrutinio);
      return { msg: respuesta.msg, enEquipo: false };
    } catch (error) {
      if (!esErrorDeRed(error)) throw error;
      cargarPendiente(await guardarEscrutinioEnEquipo(datos, cierre), escrutinio);
      return {
        msg: cierre
          ? "El acta quedó cerrada en este equipo y se enviará cuando vuelva la señal."
          : "El acta quedó guardada en este equipo y se enviará cuando vuelva la señal.",
        enEquipo: true,
      };
    }
  };

  const handleGuardar = async (event) => {
    event.preventDefault();
    setProcesando(true);
    try {
      const respuesta = await guardar();
      if (respuesta.enEquipo) alertaEnEquipo(respuesta.msg);
      else alertaExito(respuesta.msg);
    } catch (error) {
      alertaError(error);
    } finally {
      setProcesando(false);
    }
  };

  const iniciarCierre = () => {
    // la foto del E-14 no es obligatoria para cerrar: se puede cargar después
    if (!formRef.current.reportValidity()) return;
    setMostrarCierre(true);
  };

  // guarda lo último digitado y luego cierra el acta
  const confirmarCierre = async (datosCierre) => {
    setProcesando(true);
    try {
      const respuesta = await guardar(datosCierre);
      setMostrarCierre(false);
      if (respuesta.enEquipo) alertaEnEquipo(respuesta.msg);
      else alertaExito(respuesta.msg);
    } catch (error) {
      alertaError(error);
    } finally {
      setProcesando(false);
    }
  };

  const confirmarReapertura = async () => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Reabrir el escrutinio?",
      text: "El acta se podrá editar de nuevo para hacer el reconteo de la mesa.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, reabrir",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;

    setProcesando(true);
    try {
      const respuesta = await reabrirEscrutinio(escrutinio.id);
      cargarEnFormulario(respuesta.escrutinio);
      alertaExito(respuesta.msg);
    } catch (error) {
      alertaError(error);
    } finally {
      setProcesando(false);
    }
  };

  return (
    <section className={style.escrutinio}>
      <div className={style.acta}>
        <div className={style.encabezado}>
          <span className={style.icono} aria-hidden="true">
            <Icon icon="mdi:vote-outline" />
          </span>
          <h1 className={style.titulo}>Registro de Votación</h1>
          <p className={style.subtitulo}>
            Escrutinio de mesa · Acta E-14
            {nombreCorporacion && <span className={style.corporacion}>{nombreCorporacion}</span>}
          </p>
        </div>

        <div className={`row gy-4 ${style.ubicacion}`}>
          <div className="col-12 col-md-4">
            <SelectComponent
              id="corporacion"
              etiqueta="Corporación"
              claseEtiqueta={style.etiqueta}
              opciones={TIPOS_CANDIDATO}
              valor={corporacion}
              onCambiar={(valor) => setCorporacion(valor ?? "")}
              buscable={false}
              limpiable={false}
              grande
            />
          </div>
          <div className="col-12 col-md-4">
            <SelectComponent
              etiqueta="Lugar de votación"
              claseEtiqueta={style.etiqueta}
              grande
              opciones={opcionesLugar}
              onCambiar={(valor) => {
                // las mesas dependen del lugar: al cambiarlo se vuelve a elegir la mesa
                setLugar(valor);
                setMesa(null);
              }}
              valor={lugar}
            />
          </div>
          <div className="col-12 col-md-4">
            <SelectComponent
              etiqueta="Mesa"
              claseEtiqueta={style.etiqueta}
              grande
              opciones={opcionesMesaDe(lugar)}
              onCambiar={setMesa}
              valor={mesa}
              deshabilitado={!lugar}
              placeholder={lugar ? "Seleccionar…" : "Lugar Primero"}
            />
          </div>
        </div>

        {!mesaSeleccionada && (
          <p className={style.ayuda}>
            <Icon icon="mdi:information-outline" aria-hidden="true" />
            Selecciona la corporación, el lugar y la mesa para registrar el E-14.
          </p>
        )}
        {cargando && (
          <p className={style.ayuda}>
            <span className="spinner-border spinner-border-sm" aria-hidden="true" /> Cargando escrutinio…
          </p>
        )}
        {bloqueo && <div className="alert alert-danger mt-3 mb-0">{bloqueo}</div>}
        {mesaSeleccionada && !cargando && sinConexion && (
          <div className={`alert alert-info ${style.alerta}`} role="status">
            <Icon icon="mdi:wifi-off" aria-hidden="true" />
            <span>
              <strong>Sin conexión.</strong> Se muestran los datos guardados en este equipo. Puedes registrar y cerrar
              el acta: se enviará automáticamente cuando vuelva la señal.
            </span>
          </div>
        )}

        {mesaSeleccionada && !cargando && !bloqueo && (
          <form ref={formRef} onSubmit={handleGuardar} className={style.formulario}>
            {escrutinio && (
              <div className={style.estado}>
                <span className={`badge ${cerrado ? "bg-secondary" : "bg-primary"}`}>
                  {cerrado ? "Escrutinio cerrado" : "Escrutinio abierto"}
                </span>
                {pendienteLocal && (
                  <span className="badge text-bg-warning">
                    <Icon icon="mdi:cloud-upload-outline" aria-hidden="true" /> Pendiente de envío
                  </span>
                )}
                {escrutinio.tieneProblema && <span className="badge bg-danger">Reconteo solicitado</span>}
                {cerrado && !escrutinio.tieneFoto && (
                  <span className="badge text-bg-warning">Sin foto del E-14</span>
                )}
              </div>
            )}

            {escrutinio?.observacion && (
              <dl className={style.constancias}>
                <dt>Observación</dt>
                <dd>{escrutinio.observacion}</dd>
                {escrutinio.reconteoSolicitadoPor && (
                  <>
                    <dt>Solicitado por</dt>
                    <dd>{escrutinio.reconteoSolicitadoPor}</dd>
                  </>
                )}
                {escrutinio.reconteoEnRepresentacionDe && (
                  <>
                    <dt>En representación de</dt>
                    <dd>{escrutinio.reconteoEnRepresentacionDe}</dd>
                  </>
                )}
              </dl>
            )}

            {cerrado && admin && !pendienteLocal && !sinConexion && (
              <button
                type="button"
                className={`btn btn-outline-danger ${style.botonAncho}`}
                onClick={confirmarReapertura}
                disabled={procesando}
              >
                <Icon icon="mdi:lock-open-variant-outline" aria-hidden="true" /> Reabrir para reconteo
              </button>
            )}

            <fieldset disabled={cerrado || procesando} className={style.campos}>
              <h2 className={style.seccionTitulo}>
                <Icon icon="mdi:scale-balance" aria-hidden="true" /> Nivelación de la mesa
              </h2>
              <div className={style.seccion}>
                {CAMPOS_NIVELACION.map(({ campo, etiqueta }) => (
                  <CampoVotosComponent
                    key={campo}
                    id={campo}
                    etiqueta={campo === "totalVotosUrna" ? `Total votos de ${nombreCorporacion} en la urna` : etiqueta}
                    valor={totales[campo]}
                    onChange={cambiarTotal(campo)}
                  />
                ))}
              </div>

              <h2 className={style.seccionTitulo}>
                <Icon icon="mdi:account-group-outline" aria-hidden="true" /> Votación por candidato
              </h2>
              {candidatosCorporacion.length === 0 ? (
                <div className="alert alert-warning">
                  No hay candidatos de {nombreCorporacion} en el catálogo. Un administrador debe agregarlos en
                  Catálogos.
                </div>
              ) : (
                <ol className={style.candidatos}>
                  {candidatosCorporacion.map((candidato, indice) => (
                    <li key={candidato.id} className={style.candidato}>
                      <CampoVotosComponent
                        id={`votos-${candidato.id}`}
                        valor={votos[candidato.id] ?? ""}
                        onChange={cambiarVotos(candidato.id)}
                      >
                        <span className={style.numero}>{indice + 1}</span>
                        <span className={style.nombre}>{candidato.nombre}</span>
                      </CampoVotosComponent>
                    </li>
                  ))}
                </ol>
              )}

              <h2 className={style.seccionTitulo}>
                <Icon icon="mdi:ballot-outline" aria-hidden="true" /> Otros votos
              </h2>
              <div className={style.seccion}>
                {CAMPOS_ESPECIALES.map(({ campo, etiqueta }) => (
                  <CampoVotosComponent
                    key={campo}
                    id={campo}
                    etiqueta={etiqueta}
                    valor={totales[campo]}
                    onChange={cambiarTotal(campo)}
                  />
                ))}
              </div>

              <div className={style.total}>
                <span className={style.totalEtiqueta}>Suma total de votos del acta E-14</span>
                <output className={style.totalValor}>{nivelacion.sumaTotal}</output>
              </div>

              {nivelacion.cuadra ? (
                <div className={`alert alert-success ${style.alerta}`}>
                  <Icon icon="mdi:check-circle-outline" aria-hidden="true" /> El acta cuadra con los votos de la urna.
                </div>
              ) : (
                <div className={`alert alert-warning ${style.alerta}`}>
                  <Icon icon="mdi:alert-outline" aria-hidden="true" />
                  <ul className="mb-0 ps-3">
                    {nivelacion.novedades.map((novedad) => (
                      <li key={novedad}>{novedad}</li>
                    ))}
                  </ul>
                </div>
              )}
            </fieldset>

            <FotoE14Component
              fotoUrl={fotoVista}
              onSeleccionar={seleccionarFoto}
              // cerrada: solo se puede cargar si quedó sin foto (cambiarla exige reabrir)
              deshabilitado={procesando || (cerrado && Boolean(fotoVista))}
            />

            {!cerrado && (
              <div className={style.acciones}>
                <button
                  type="submit"
                  className={`btn ${style.boton} ${style.botonPrincipal}`}
                  disabled={procesando || candidatosCorporacion.length === 0}
                >
                  <Icon icon="mdi:content-save-outline" aria-hidden="true" />
                  {procesando ? "Guardando…" : "Guardar avance"}
                </button>
                <button
                  type="button"
                  className={`btn ${style.boton} ${style.botonCierre}`}
                  onClick={iniciarCierre}
                  disabled={procesando || candidatosCorporacion.length === 0}
                >
                  <Icon icon="mdi:lock-check-outline" aria-hidden="true" />
                  Cerrar escrutinio
                </button>
              </div>
            )}
          </form>
        )}

        <Link to="/resultado" className={`btn btn-outline-secondary ${style.botonAncho}`}>
          <Icon icon="mdi:chart-bar" aria-hidden="true" /> Ver resultados
        </Link>
      </div>

      <ModalCierreComponent
        show={mostrarCierre}
        onHide={() => setMostrarCierre(false)}
        onConfirmar={confirmarCierre}
        novedades={nivelacion.novedades}
        sinFoto={!fotoVista}
        procesando={procesando}
      />
    </section>
  );
};

export default EscrutinioComponent;
